/**
 * Life4Earth V2 — Media URL resolver (the single source of truth for image src)
 *
 * THE PROBLEM THIS SOLVES:
 * Photos are stored in INCONSISTENT formats across the app:
 *   - a bare object key                       e.g. "heroes/{guid}/x.jpg"
 *   - a relative FileStorage path             e.g. "/api/FileStorage/file/heroes/{guid}/x.jpg"
 *   - a double-prefixed value (a past bug)    e.g. "/api/FileStorage/file//api/FileStorage/file/heroes/..."
 *   - a host-prefixed FileStorage URL         e.g. "http://localhost:5295/api/FileStorage/file/heroes/..."
 *   - a genuine external URL                  e.g. "https://lh3.googleusercontent.com/..." (Google avatar, news image)
 *
 * Different render sites historically built the `src` differently, so DB patches
 * kept fixing one surface and breaking another. This helper ends that: route
 * EVERY image render site through `mediaSrc()` and it produces a correct,
 * same-origin URL regardless of what is stored — no DB migration required.
 *
 * TOLERANCE LOGIC (in order):
 *   - falsy/empty                        → null
 *   - contains "/api/FileStorage/file/"  → take everything AFTER the LAST occurrence
 *                                          as the key (collapses single-, double-, and
 *                                          host-prefixed forms), strip leading slashes,
 *                                          rebuild as `${API_BASE_URL}/api/FileStorage/file/${key}`
 *   - starts with http://, https://,     → genuine external URL or inline source
 *     data:, or blob:                      → return unchanged
 *   - otherwise                          → bare object key → strip leading slashes,
 *                                          return `${API_BASE_URL}/api/FileStorage/file/${key}`
 *
 * API_BASE_URL is "" in the production build, so the rebuilt URL is a SAME-ORIGIN
 * relative path ("/api/FileStorage/file/<key>"), which is what the reverse proxy
 * expects. In dev it is "http://localhost:5295".
 *
 * Any "?v=..." cache-buster that is part of the stored value rides along on the
 * key/query and is preserved.
 *
 * Framework-light by design (no React) so it can be used from components, hooks,
 * and plain utility code alike.
 */

import { API_BASE_URL } from './client';

const FILE_PATH = '/api/FileStorage/file/';

/**
 * Resolve any stored photo value into a renderable `src`.
 * Returns null for empty/falsy input so callers can fall back to an initial/placeholder.
 */
export function mediaSrc(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;

  // FileStorage form (single-, double-, or host-prefixed). Take everything after
  // the LAST occurrence of the marker so any number of prefixes collapse to one.
  const lastIdx = value.lastIndexOf(FILE_PATH);
  if (lastIdx !== -1) {
    const key = value.slice(lastIdx + FILE_PATH.length).replace(/^\/+/, '');
    return `${API_BASE_URL}${FILE_PATH}${key}`;
  }

  // Genuine external URL (Google avatar, news image, etc.) or an inline
  // data:/blob: source (upload preview, pasted image) — leave untouched.
  if (/^(https?:\/\/|data:|blob:)/i.test(value)) {
    return value;
  }

  // Bare object key.
  const key = value.replace(/^\/+/, '');
  return `${API_BASE_URL}${FILE_PATH}${key}`;
}
